import type {
  SimBrotherhoodCheck,
  SimBrotherhoodProblem,
  SimBrotherhoodRequest,
  SimCareerView,
  SimChoiceResponse,
  SimBrotherhoodInvite,
  SimChallengeResults,
  SimDailyResponse,
  SimDailyResults,
  SimMyBrotherhood,
  SimDecisionResults,
  SimErrorCode,
  SimEventResponse,
  SimMe,
  SimMetrics,
  SimRegisterRequest,
  SimReportGroup,
  SimReportReason,
  SimShareRequest,
  SimShareResponse,
  SimSwitches,
  SimSwitchesView,
  SimTramoPuzzle,
  SimTramoResponse,
} from '@cruz/shared';
import { SIM_NICKNAME_MESSAGES, SIM_PASSWORD_MESSAGES } from '@cruz/shared';

/*
 * Client of /api/sim. The session is an httpOnly cookie the browser sends by itself;
 * every change carries the X-Cruz-Sim header (the server's CSRF check).
 */

export interface SimErrorBody {
  error: SimErrorCode | 'not_found';
  reason?: string;
  fields?: string[];
  problems?: SimBrotherhoodProblem[];
}

export class SimApiError extends Error {
  override name = 'SimApiError';
  readonly status: number;
  readonly body: SimErrorBody | null;
  constructor(status: number, body: SimErrorBody | null) {
    super(body?.error ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
  get code(): string | null {
    return this.body?.error ?? null;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function simRequest<T>(
  method: Method,
  path: string,
  body?: unknown,
  {
    retries = method === 'GET' ? 3 : 0,
    headers = {},
  }: { retries?: number; headers?: Record<string, string> } = {},
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    let error: SimApiError;
    try {
      const res = await fetch(`/api/sim${path}`, {
        method,
        credentials: 'same-origin',
        headers: {
          ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(method === 'GET' ? {} : { 'x-cruz-sim': '1' }),
          ...headers,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      if (res.ok) return (await res.json()) as T;
      let parsed: SimErrorBody | null = null;
      try {
        parsed = (await res.json()) as SimErrorBody;
      } catch {
        // Not JSON (a proxy page): keep the status
      }
      error = new SimApiError(res.status, parsed);
    } catch {
      error = new SimApiError(0, null);
    }
    const retriable = error.status === 0 || [502, 503, 504].includes(error.status);
    if (!retriable || attempt >= retries) throw error;
    await sleep(600 * 2 ** attempt);
  }
}

export const simApi = {
  me: () => simRequest<SimMe>('GET', '/me'),
  guest: (ref: string | null = null) => simRequest<SimMe>('POST', '/guest', ref ? { ref } : {}),
  nickname: (nickname: string) =>
    simRequest<{ available: boolean; reason: string | null }>(
      'GET',
      `/nickname?nickname=${encodeURIComponent(nickname)}`,
    ),
  register: (body: SimRegisterRequest) => simRequest<SimMe>('POST', '/register', body),
  login: (email: string, password: string) =>
    simRequest<SimMe>('POST', '/login', { email, password }),
  logout: () => simRequest<{ ok: true }>('POST', '/logout'),
  verify: (token: string) => simRequest<{ ok: true }>('POST', '/verify', { token }),
  resendVerification: () => simRequest<{ ok: true }>('POST', '/verify/resend'),
  forgot: (email: string) => simRequest<{ ok: true }>('POST', '/password/forgot', { email }),
  reset: (token: string, password: string) =>
    simRequest<{ ok: true }>('POST', '/password/reset', { token, password }),
  preferences: (marketing: boolean) => simRequest<SimMe>('PUT', '/me/preferences', { marketing }),
  deleteAccount: (password: string) =>
    simRequest<{ ok: true }>('DELETE', '/me', password ? { password } : {}),
  unsubscribe: (key: string) => simRequest<{ ok: true }>('POST', '/unsubscribe', { key }),
  checkBrotherhood: (body: SimBrotherhoodRequest) =>
    simRequest<SimBrotherhoodCheck>('POST', '/brotherhood/check', body),
  createCareer: (body: SimBrotherhoodRequest) => simRequest<SimCareerView>('POST', '/career', body),
  career: () => simRequest<SimCareerView>('GET', '/career'),
  event: () => simRequest<SimEventResponse>('GET', '/career/event'),
  choose: (idx: number, choice: number) =>
    simRequest<SimChoiceResponse>('POST', `/career/event/${idx}/choice`, { choice }),
  tramo: () => simRequest<{ puzzle: SimTramoPuzzle }>('GET', '/career/tramo'),
  playTramo: (taps: string[], ms: number) =>
    simRequest<SimTramoResponse>('POST', '/career/tramo', { taps, ms }),
  share: (body: SimShareRequest) => simRequest<SimShareResponse>('POST', '/shares', body),
  shared: (id: string, voter: string | null = null) =>
    simRequest<SimShareResponse>(
      'GET',
      `/shares/${id}${voter ? `?voter=${encodeURIComponent(voter)}` : ''}`,
    ),
  vote: (id: string, choice: number, voter: string) =>
    simRequest<SimDecisionResults>('POST', `/shares/${id}/vote`, { choice, voter }),
  brotherhood: () => simRequest<SimMyBrotherhood>('GET', '/brotherhood'),
  invite: () => simRequest<SimMyBrotherhood>('POST', '/brotherhood/invite'),
  invitation: (code: string) => simRequest<SimBrotherhoodInvite>('GET', `/brotherhoods/${code}`),
  join: (code: string) => simRequest<SimCareerView>('POST', `/brotherhoods/${code}/join`),
  daily: (voter: string | null) =>
    simRequest<SimDailyResponse>(
      'GET',
      `/daily${voter ? `?voter=${encodeURIComponent(voter)}` : ''}`,
    ),
  playDaily: (day: string, taps: string[], ms: number, voter: string) =>
    simRequest<SimDailyResults>('POST', '/daily', { day, taps, ms, voter }),
  playChallenge: (id: string, taps: string[], ms: number, voter: string) =>
    simRequest<SimChallengeResults>('POST', `/shares/${id}/tramo`, { taps, ms, voter }),
  reportEvent: (idx: number, reason: SimReportReason, comment: string) =>
    simRequest<{ ok: true }>('POST', '/reports', { idx, reason, comment }),
  reportShare: (id: string, reason: SimReportReason, comment: string) =>
    simRequest<{ ok: true }>('POST', `/shares/${id}/report`, { reason, comment }),
};

/** The review panel: its key goes as a Bearer token, never in a cookie. */
export function simAdminApi(key: string) {
  const o = { headers: { authorization: `Bearer ${key}` } };
  return {
    reports: () => simRequest<{ groups: SimReportGroup[] }>('GET', '/admin/reports', undefined, o),
    resolve: (key: string, action: 'accept' | 'dismiss') =>
      simRequest<{ ok: true }>('POST', '/admin/reports/resolve', { key, action }, o),
    switches: () => simRequest<SimSwitchesView>('GET', '/admin/switches', undefined, o),
    setSwitches: (patch: Partial<SimSwitches>) =>
      simRequest<SimSwitchesView>('PUT', '/admin/switches', patch, o),
    metrics: (days: number) =>
      simRequest<SimMetrics>('GET', `/admin/metrics?days=${days}`, undefined, o),
  };
}

const MESSAGES: Partial<Record<string, string>> = {
  invalid_credentials: 'El correo o la contraseña no son correctos.',
  locked: 'Demasiados intentos seguidos. Espera un cuarto de hora y vuelve a probar.',
  email_taken: 'Ya hay una cuenta con ese correo. ¿Quieres entrar?',
  nickname_taken: SIM_NICKNAME_MESSAGES.taken,
  token_invalid: 'Este enlace ya no sirve: ha caducado o ya se usó. Pide otro.',
  csrf: 'No se ha podido enviar. Recarga la página y vuelve a probar.',
  unauthorized: 'Tienes que entrar en tu cuenta.',
  career_exists: 'Ya tienes una partida en marcha.',
  not_waiting: 'Ese evento ya estaba respondido.',
  tramo_closed: 'El tramo de hoy ya está hecho. Mañana, otro.',
  daily_closed: 'Ese tramo ya es de otro día. Recarga la página para jugar el de hoy.',
  brotherhood_full: 'Esta hermandad ya está completa.',
  no_career: 'Todavía no has fundado tu hermandad.',
  not_found: 'No lo encontramos.',
  register_required: 'Para esto, guarda antes tu carrera con una cuenta.',
};

/** A sentence in Spanish for any error of the API. */
export function simErrorMessage(err: unknown): string {
  if (!(err instanceof SimApiError)) return 'Algo ha fallado. Vuelve a probar.';
  if (err.status === 0) return 'No hay conexión. Comprueba la cobertura y vuelve a probar.';
  if (err.status === 429) return 'Vas muy deprisa. Espera un minuto y vuelve a probar.';
  const b = err.body;
  if (b?.error === 'password_invalid' && b.reason)
    return (
      SIM_PASSWORD_MESSAGES[b.reason as keyof typeof SIM_PASSWORD_MESSAGES] ??
      'Elige otra contraseña.'
    );
  if (b?.error === 'nickname_invalid' && b.reason)
    return (
      SIM_NICKNAME_MESSAGES[b.reason as keyof typeof SIM_NICKNAME_MESSAGES] ?? 'Elige otro apodo.'
    );
  if (b?.error === 'invalid_request') return 'Revisa los datos del formulario.';
  if (b?.error && MESSAGES[b.error]) return MESSAGES[b.error]!;
  if (err.status >= 500) return 'El servidor no responde ahora mismo. Vuelve a probar en un rato.';
  return 'Algo ha fallado. Vuelve a probar.';
}
